import {
  useEffect, useCallback, useRef, useMemo,
} from 'react';
import { createUseStyles } from 'react-jss';
import { useStore } from 'react-redux';

import Analytics from 'app/lib/Analytics/Analytics';
import MediaPlayer from 'app/lib/MediaPlayer';
import Resource from 'app/lib/Resource';
import { useAppContext } from 'app/lib/AppContext';

import Header from 'app/components/common/header/Header';
import Layout from 'app/components/common/Layout';
import LoaderAnimation from 'app/components/common/LoaderAnimation';
import * as Flipbook from 'app/components/common/flipbook/Flipbook';
import PageBuilder from 'app/components/screens/page-builder/PageBuilder';
import Toolbar from 'app/components/common/toolbar/Toolbar';
import Hint from 'app/components/common/Hint';
import GetApp from 'app/components/common/GetApp';

import * as Modal from 'app/components/common/modal/Modal';
import Share from 'app/components/common/modals/share/Share';
import Toast from 'app/components/common/modals/toast/Toast';
import Gift from 'app/components/common/modals/gift/Gift';
import GiftError from 'app/components/common/modals/gift-error/GiftError';
import GiftTerms from 'app/components/common/modals/gift-terms/GiftTerms';
import GiftUseLater from 'app/components/common/modals/GiftUseLater';
import Invitation from 'app/components/common/modals/Invitation';

import { useSelector, useDispatcher } from 'app/store/utils/redux/hooks';

const CardPlayer = () => {
  const dispatcher = useDispatcher();
  const classes = useStyles();

  const store = useStore();
  const cardStatus = useSelector((state) => state?.card?.meta?.status || 'init');
  const cardError = useSelector((state) => state?.card?.meta?.errors?.[0]);
  const cardPages = useSelector((state) => state?.card?.pages || []);

  const {
    identity, bowser, mode, pages,
  } = useAppContext();

  const flipbookRef: any = useRef();
  const errorTimeoutRef: any = useRef();

  const handleFlipbookFlip = useCallback((event: any) => {
    const { currentPage, visiblePages, countPages } = event;
    dispatcher.flipbook.updateState(currentPage, visiblePages, countPages);
  }, []);

  const handleResize = useCallback(() => {
    const body: HTMLBodyElement = (document.body as HTMLBodyElement);
    if (window.innerHeight < 500) {
      body.style.height = '100vh';
    } else {
      body.style.height = `${window.innerHeight}px`;
    }
    if (!body.getAttribute('data-layout')) {
      body.style.overflow = 'hidden';
      body.setAttribute('data-layout', 'fixed');
    }
  }, []);

  const handleActiveSwitch = useCallback((event: any) => {
    const { type } = event;
    const isIos = bowser.os.name?.toLowerCase() === 'ios';
    const isSafari = bowser.browser.name?.toLowerCase() === 'safari';

    if (type === 'blur' && !(isIos && isSafari)) {
      MediaPlayer.pauseAll('app');
    }
    if (type === 'blur' && isIos && isSafari) {
      MediaPlayer.muteAll('app');
    }
    if (type === 'focus' && !(isIos && isSafari)) {
      MediaPlayer.resumeAll('app');
    }
    if (type === 'focus' && isIos && isSafari) {
      MediaPlayer.unmuteAll('app');
    }
  }, []);

  const handleErrorLoading = useCallback((message: string) => {
    dispatcher.card.logLoadingError(message);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    if (!document.body.getAttribute('data-layout')) {
      handleResize();
    }
    window.addEventListener('resize', handleResize);
    window.addEventListener('blur', handleActiveSwitch);
    window.addEventListener('focus', handleActiveSwitch);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('blur', handleActiveSwitch);
      window.removeEventListener('focus', handleActiveSwitch);
    };
  }, [handleActiveSwitch, handleResize]);

  useEffect(() => {
    const state = store.getState();
    const cardId = state.card.common.id;
    const resources = state.card?.resources || [];
    if (cardStatus === 'init') {
      dispatcher.card.loadData(
        identity.type,
        identity.param,
        identity.code,
        {
          mode,
          pages,
        },
      );
    }
    if (cardStatus === 'data-loaded') {
      const handleResourceLoaded = ((event: any) => {
        const { payload } = event;
        const { loaded, total } = payload;
        const percent = loaded / total;
        dispatcher.card.loadProgress(percent);
      });
      const handleAllResourcesLoaded = () => {
        Resource.removeEventListener(Resource.EVENT.ALL_RESOURCES_LOADED, handleAllResourcesLoaded);
        Resource.removeEventListener(Resource.EVENT.RESOURCE_LOADED, handleResourceLoaded);
        dispatcher.card.setStatus('ready');
      };
      Analytics.setCardId(cardId as number);
      dispatcher.card.setStatus('resources-loading');
      Resource.addEventListener(Resource.EVENT.ALL_RESOURCES_LOADED, handleAllResourcesLoaded);
      Resource.addEventListener(Resource.EVENT.RESOURCE_LOADED, handleResourceLoaded);
      Resource.load([...resources]);
    }
  }, [cardStatus, identity, pages]);

  useEffect(() => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = setTimeout(() => {
      handleErrorLoading(`Error loading resource (OS: ${bowser.os.name}, Browser: ${bowser.browser.name})`);
    }, 10000);
    if (errorTimeoutRef.current && cardStatus === 'ready') {
      clearTimeout(errorTimeoutRef.current);
    }
  }, [cardStatus]);

  return (
    <Layout>
      <Header />
      {!cardError && (
        <LoaderAnimation>
          <Flipbook.Container
            width={32}
            ratio={3 / 2}
            unit="rem"
            onFlip={handleFlipbookFlip}
            initConfig={{ enabledFlip: false }}
            controllerRef={flipbookRef}
          >
            {cardPages.map((page) => (
              <PageBuilder key={page.id} />
            ))}
          </Flipbook.Container>
          <Hint flipbookRef={flipbookRef} />
          <Toolbar />
          <GetApp />
        </LoaderAnimation>
      )}
      {cardError && (
        <div className={classes.fetchError}>
          <span>{cardError.substring(0, 1)}</span>
          {cardError.substring(1)}
        </div>
      )}
      <Modal.Container>
        <GiftError />
        <Gift />
        <GiftTerms />
        <GiftUseLater />
        <Invitation />
        <Share />
        <Toast />
      </Modal.Container>
    </Layout>
  );
};

const useStyles = createUseStyles({
  fetchError: {
    '&': {
      position: 'absolute',
      textAlign: 'center',
      fontSize: '1.8rem',
      top: '30vh',
      left: '50vw',
      width: 'calc(100vw - 4rem)',
      transform: 'translate(-50%)',
    },
    '& span': {
      textTransform: 'uppercase',
    },
  },
});

export default CardPlayer;
